import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { formatINR } from "../data/dummyLedgerTransactionsData";
import {
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
} from "react-icons/fa";
import { TableRowsSkeleton } from "../../../Component/Skeleton";

export default function LedgerTransactionTable({
  transactions = [],
  monthLabel = "",
  openingBalance = 0,
  openingType = "Dr",
  totalDebit = 0,
  totalCredit = 0,
  closingBalance = 0,
  closingType = "Dr",
  searchTerm = "",
  onSearchChange,
  onExportExcel,
  onExportPDF,
  onPrint,
  loading = false,
}) {
  const navigate = useNavigate();
  // Filter transactions by search term
  const filteredList = useMemo(() => {
    if (!searchTerm.trim()) return transactions;
    const term = searchTerm.toLowerCase();
    return transactions.filter(
      (tx) =>
        (tx.voucherNo && tx.voucherNo.toLowerCase().includes(term)) ||
        (tx.particulars && tx.particulars.toLowerCase().includes(term)) ||
        (tx.narration && tx.narration.toLowerCase().includes(term)) ||
        (tx.referenceNo && tx.referenceNo.toLowerCase().includes(term)) ||
        (tx.voucherType && tx.voucherType.toLowerCase().includes(term))
    );
  }, [transactions, searchTerm]);

  const getVoucherBadgeClass = (type) => {
    const t = String(type || "").toLowerCase();
    if (t.includes("payment")) return "lt_badge_payment";
    if (t.includes("receipt")) return "lt_badge_receipt";
    return "lt_badge_contra";
  };

  return (
    <div className="lt_table_card">
      <div className="lt_table_toolbar">
        <h3 className="lt_table_title">Transactions - {monthLabel}</h3>

        <div className="lt_toolbar_right">
          {/* Search Box */}
          <div className="lt_search_box">
            <FaSearch className="lt_search_icon" />
            <input
              type="text"
              className="lt_search_input"
              placeholder="Search narration, voucher no..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          {/* Export Actions */}
          <button
            type="button"
            className="lt_export_btn btn_excel"
            onClick={onExportExcel}
            title="Export to Excel"
          >
            <FaFileExcel />
            Export Excel
          </button>
          <button
            type="button"
            className="lt_export_btn btn_pdf"
            onClick={onExportPDF}
            title="Export to PDF"
          >
            <FaFilePdf />
            Export PDF
          </button>
          <button
            type="button"
            className="lt_export_btn btn_print"
            onClick={onPrint}
            title="Print Transactions"
          >
            <FaPrint />
            Print
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="lt_table_wrapper">
        <table className="lt_custom_table">
          <thead>
            <tr>
              <th className="lt_text_center" style={{ width: "4%" }}>
                S.No.
              </th>
              <th style={{ width: "12%" }}>Date</th>
              <th style={{ width: "9%" }}>Voucher No.</th>
              <th style={{ width: "8%" }}>Voucher Type</th>
              <th style={{ width: "23%" }}>Particulars / Narration</th>
              <th style={{ width: "12%" }}>Reference No.</th>
              <th className="lt_table_num" style={{ width: "10%" }}>
                Debit (₹)
              </th>
              <th className="lt_table_num" style={{ width: "10%" }}>
                Credit (₹)
              </th>
              <th className="lt_table_num" style={{ width: "12%" }}>
                Balance (₹)
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Opening Balance Line */}
            <tr className="lt_opening_row">
              <td className="lt_text_center"></td>
              <td style={{ fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                Opening Balance
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td className="lt_table_num">
                {openingType === "Dr" && Number(openingBalance) > 0 ? (
                  <span style={{ fontWeight: 600 }}>{formatINR(openingBalance)}</span>
                ) : (
                  ""
                )}
              </td>
              <td className="lt_table_num">
                {openingType === "Cr" && Number(openingBalance) > 0 ? (
                  <span style={{ fontWeight: 600 }}>{formatINR(openingBalance)}</span>
                ) : (
                  ""
                )}
              </td>
              <td className="lt_table_num"></td>
            </tr>

            {loading && <TableRowsSkeleton columns={9} rows={5} />}

            {!loading && filteredList.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center", padding: "16px", color: "#a0aec0" }}>
                  No transactions found for the selected period.
                </td>
              </tr>
            ) : (
              !loading &&
              filteredList.map((tx, idx) => {
                const isCr = tx.balanceType === "Cr";
                const isBlankVoucher = !tx.voucherNo || tx.voucherNo === "-";
                const isClickable = Boolean(tx.voucherId || (!isBlankVoucher && tx.voucherNo));
                return (
                  <tr
                    key={tx.id || tx.sNo || idx}
                    onClick={() => {
                      if (tx.voucherId) {
                        navigate(
                          `/transactions/modify-voucher?voucherId=${tx.voucherId}`
                        );
                      } else if (!isBlankVoucher && tx.voucherNo) {
                        navigate(
                          `/transactions/modify-voucher?voucherNo=${encodeURIComponent(
                            tx.voucherNo
                          )}`
                        );
                      }
                    }}
                    style={{ cursor: isClickable ? "pointer" : "default" }}
                  >
                    <td className="lt_text_center">{tx.sNo || idx + 1}</td>
                    <td>{tx.date}</td>
                    <td style={{ fontWeight: isBlankVoucher ? 400 : 600, color: isBlankVoucher ? "#a0aec0" : "#1a202c" }}>
                      {!isBlankVoucher ? (
                        <span
                          className="lt_voucher_click_link"
                          title="Click to view & modify this voucher in Transactions"
                          style={{
                            cursor: "pointer",
                            color: "var(--primary-teal)",
                            textDecoration: "none",
                            fontWeight: 700,
                          }}
                        >
                          {tx.voucherNo}
                        </span>
                      ) : (
                        ""
                      )}
                    </td>
                    <td>
                      {isBlankVoucher ? (
                        ""
                      ) : (
                        <span className={`lt_badge ${getVoucherBadgeClass(tx.voucherType)}`}>
                          {tx.voucherType}
                        </span>
                      )}
                    </td>
                    <td>
                      <div>
                        <span style={{ fontWeight: isBlankVoucher ? 400 : 500, color: isBlankVoucher ? "#a0aec0" : "#2d3748" }}>
                          {tx.particulars || ""}
                        </span>
                      </div>
                      {tx.narration && tx.narration !== tx.particulars && (
                        <div style={{ fontSize: "10px", color: "#718096", marginTop: "1px" }}>
                          {tx.narration}
                        </div>
                      )}
                    </td>
                    <td style={{ color: "#4a5568" }}>{tx.referenceNo || ""}</td>
                    <td className="lt_table_num">
                      {tx.debit > 0 ? formatINR(tx.debit) : ""}
                    </td>
                    <td className="lt_table_num">
                      {tx.credit > 0 ? formatINR(tx.credit) : ""}
                    </td>
                    <td className="lt_table_num">
                      {Number(tx.balance) !== 0 ? (
                        <>
                          <span style={{ fontWeight: 600 }}>{formatINR(Math.abs(tx.balance))}</span>
                          <span className={isCr ? "drcr_text_cr" : "drcr_text_dr"}>
                            {" "}{tx.balanceType || "Dr"}
                          </span>
                        </>
                      ) : (
                        ""
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr className="lt_table_total_row">
              <td colSpan="6" style={{ textAlign: "center", fontWeight: "700" }}>
                Total
              </td>
              <td className="lt_table_num">{formatINR(totalDebit)}</td>
              <td className="lt_table_num">{formatINR(totalCredit)}</td>
              <td className="lt_table_num">
                <span style={{ fontWeight: 700 }}>{formatINR(closingBalance)}</span>
                <span className={closingType === "Cr" ? "drcr_text_cr" : "drcr_text_dr"}>
                  {" "}{closingType || "Dr"}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
